import { useEffect, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import type { TimingVendor } from '@racedash/shared'
import {
  countSessionsForSeries,
  createSeries,
  deleteSeries,
  updateSeries,
  useSeries,
} from '../data/catalog'
import type { Series } from '../data/catalog'
import ConfirmDelete from '../components/ConfirmDelete'

const VENDORS: TimingVendor[] = ['ris', 'tsl']

/**
 * Series are shared across events — a FunCup weekend and a Ligier weekend at
 * the same circuit are one event hosting two series, and each session names
 * which it belongs to.
 *
 * Reading and editing are separated, as everywhere else: the table is what
 * exists, an action column opens a dialog, and the same dialog serves adding
 * and editing.
 */
export default function SeriesPage() {
  const { series, error } = useSeries()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<Series | null>(null)
  const [removing, setRemoving] = useState<Series | null>(null)

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={1} size="h2">
            Series
          </Title>
          <Text c="dimmed" size="sm">
            Championships. A session belongs to one; an event may host several.
          </Text>
        </div>
        <Button onClick={() => setAdding(true)}>New series</Button>
      </Group>

      {error && <Alert color="red">Could not load the series list: {error}</Alert>}

      {series && series.length > 0 && (
        <Paper withBorder>
          <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Series</Table.Th>
                <Table.Th>Names on the wire</Table.Th>
                <Table.Th w={150} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {series.map((s) => (
                <Table.Tr key={s.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {s.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="md">
                      {Object.entries(s.vendorNames ?? {}).map(([v, names]) => (
                        <Text key={v} size="xs" c="dimmed" ff="monospace">
                          {v}: {(names as string[]).join(', ')}
                        </Text>
                      ))}
                      {Object.keys(s.vendorNames ?? {}).length === 0 && (
                        <Text size="xs" c="dimmed">
                          none — this series can never be matched
                        </Text>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td align="right">
                    <Group gap={4} justify="flex-end" wrap="nowrap">
                      <Button variant="subtle" size="compact-sm" onClick={() => setEditing(s)}>
                        Edit
                      </Button>
                      <Button
                        variant="subtle"
                        color="red"
                        size="compact-sm"
                        onClick={() => setRemoving(s)}
                      >
                        Delete
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
      {!error && series?.length === 0 && (
        <Paper withBorder p="xl">
          <Text c="dimmed" ta="center">
            No series yet.
          </Text>
        </Paper>
      )}

      <ConfirmDelete
        opened={removing !== null}
        onClose={() => setRemoving(null)}
        what="series"
        name={removing?.name ?? ''}
        referenceNoun="session"
        countReferences={() => countSessionsForSeries(removing!.id)}
        onDelete={() => deleteSeries(removing!.id)}
      />

      <SeriesDialog opened={adding} onClose={() => setAdding(false)} />
      <SeriesDialog
        opened={editing !== null}
        onClose={() => setEditing(null)}
        series={editing ?? undefined}
      />
    </Stack>
  )
}

/** One name a vendor uses. Flat, because a map of arrays cannot be edited row
 *  by row and the reader is editing rows. */
interface Alias {
  vendor: TimingVendor
  name: string
}

interface Draft {
  name: string
  aliases: Alias[]
}

/** Compared as a string: the aliases are an ordered list of two-field rows, and
 *  reordering them is a change worth noticing since it is visible. */
function key(d: Draft): string {
  return JSON.stringify([d.name.trim(), d.aliases.map((a) => [a.vendor, a.name.trim()])])
}

function draftOf(series: Series): Draft {
  const aliases: Alias[] = []
  for (const [vendor, names] of Object.entries(series.vendorNames ?? {})) {
    for (const name of (names as string[]) ?? []) {
      aliases.push({ vendor: vendor as TimingVendor, name })
    }
  }
  return { name: series.name, aliases }
}

/** Back to the stored shape: one key per vendor, each holding its names. */
function vendorNamesOf(draft: Draft): Partial<Record<TimingVendor, string[]>> {
  const out: Partial<Record<TimingVendor, string[]>> = {}
  for (const a of draft.aliases) {
    const name = a.name.trim()
    if (!name) continue
    const list = out[a.vendor] ?? []
    // The same string twice for one vendor matches nothing extra and reads as
    // a mistake, so it is dropped rather than stored.
    if (!list.some((n) => n.toLowerCase() === name.toLowerCase())) list.push(name)
    out[a.vendor] = list
  }
  return out
}

function SeriesDialog({
  opened,
  onClose,
  series,
}: {
  opened: boolean
  onClose: () => void
  series?: Series
}) {
  const editing = Boolean(series)
  const [draft, setDraft] = useState<Draft>({ name: '', aliases: [] })
  const [baseline, setBaseline] = useState<Draft>({ name: '', aliases: [] })
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  useEffect(() => {
    if (!opened) return
    setFailed(null)
    const next: Draft = series
      ? draftOf(series)
      : // A new series starts with one empty row, because a series with no
        // vendor name can never be matched to anything on the wire.
        { name: '', aliases: [{ vendor: 'ris', name: '' }] }
    setDraft(next)
    setBaseline(next)
  }, [opened, series])

  const changed = key(draft) !== key(baseline)
  const complete = Boolean(draft.name.trim())
  const canSubmit = complete && (!editing || changed)

  const named = vendorNamesOf(draft)
  const willMatchNothing = Object.keys(named).length === 0

  function setAlias(i: number, patch: Partial<Alias>) {
    setDraft((d) => ({ ...d, aliases: d.aliases.map((a, n) => (n === i ? { ...a, ...patch } : a)) }))
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (busy || !canSubmit) return
    setBusy(true)
    setFailed(null)
    try {
      // An empty alias field falls back to the display name, which is right far
      // more often than it is wrong and saves typing the same thing twice.
      const vendorNames = willMatchNothing
        ? { [draft.aliases[0]?.vendor ?? 'ris']: [draft.name.trim()] }
        : named
      const input = { name: draft.name.trim(), vendorNames }
      if (series) {
        await updateSeries(series.id, input)
      } else {
        await createSeries(input)
      }
      onClose()
    } catch (err) {
      setFailed(err instanceof Error ? err.message : 'Could not save the series.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={() => !busy && onClose()}
      title={editing ? `Edit ${series?.name}` : 'New series'}
      size="lg"
    >
      <form onSubmit={(e) => void submit(e)}>
        <Stack gap="md">
          {failed && <Alert color="red">{failed}</Alert>}

          <TextInput
            label="Display name"
            value={draft.name}
            // The value is read HERE, not inside the updater. React clears
            // currentTarget once the handler returns, and a functional updater
            // runs afterwards — so reading it in there threw on the first
            // keystroke and the field could not be typed in at all.
            onChange={(e) => {
              const name = e.currentTarget.value
              setDraft((d) => ({ ...d, name }))
            }}
            placeholder="Fun Cup Belgium"
            data-autofocus
            required
          />

          <div>
            <Text size="sm" fw={500}>
              Names on the wire
            </Text>
            <Text size="xs" c="dimmed" mb="xs">
              Matching happens on these rather than on the display name, because the vendor's
              string is what actually arrives. A vendor may report more than one spelling, and each
              needs listing.
            </Text>

            <Stack gap="xs">
              {draft.aliases.map((a, i) => (
                <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
                  <Select
                    w={110}
                    data={VENDORS}
                    value={a.vendor}
                    onChange={(v) => setAlias(i, { vendor: (v as TimingVendor) ?? 'ris' })}
                    allowDeselect={false}
                  />
                  <TextInput
                    style={{ flex: 1 }}
                    value={a.name}
                    onChange={(e) => setAlias(i, { name: e.currentTarget.value })}
                    placeholder={draft.name.trim() || 'Exactly as the stream reports it'}
                    styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
                  />
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="lg"
                    aria-label="Remove this name"
                    onClick={() =>
                      setDraft((d) => ({ ...d, aliases: d.aliases.filter((_, n) => n !== i) }))
                    }
                  >
                    ×
                  </ActionIcon>
                </Group>
              ))}
            </Stack>

            <Button
              variant="subtle"
              size="compact-sm"
              mt="xs"
              onClick={() =>
                setDraft((d) => ({ ...d, aliases: [...d.aliases, { vendor: 'ris', name: '' }] }))
              }
            >
              Add a name
            </Button>
          </div>

          {willMatchNothing && (
            <Alert color="yellow" variant="light">
              No name given, so the display name will be used. A series with nothing recorded here
              can never be matched to a live stream.
            </Alert>
          )}

          <Group justify="flex-end" mt="xs">
            <Button variant="default" type="button" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" loading={busy} disabled={!canSubmit}>
              {editing ? 'Save changes' : 'Add series'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
